<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('stock_management.storage_losses.title')"
      :grid-ref="$refs.storageLossGrid"
      :show-add="canEdit"
      :show-filter="false"
      @add="goToCreate"
    />

    <DxDataGrid
      ref="storageLossGrid"
      :data-source="dataSource"
      :column-min-width="50"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="{ paging: true, filtering: true, sorting: true }"
      :element-attr="{ class: 'smagria-grid storage-loss-grid' }"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
      @exporting="onExporting"
      @option-changed="onOptionChanged"
    >
      <DxColumn
        :width="80"
        alignment="center"
        :fixed="true"
        fixed-position="left"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="pdfCell"
      />
      <template #pdfCell="{ data }">
        <div v-if="data.data.document_uuid" class="transaction-pdf-link" @click.prevent="downloadPdf(data.data)">
          <img src="~@/assets/images/desktop/icons/controls/pdf.svg" class="icon-svg danger-filter" />
        </div>
      </template>

      <DxColumn
        data-field="storage_loss_date"
        :filter-operations="['contains']"
        :caption="$t('stock_management.transactions.date')"
        data-type="date"
        :format="getDxGridDefaultDateFormat"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="legal_entity_name"
        :filter-operations="['contains']"
        :caption="$t('common.legal_entity_name')"
        data-type="string"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="warehouse_name"
        :filter-operations="['contains']"
        :caption="$t('common.warehouse')"
        data-type="string"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="product_name"
        :filter-operations="['contains']"
        :caption="$t('common.product')"
        data-type="string"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="engineer_name"
        :filter-operations="['contains']"
        :caption="$t('stock_management.storage_losses.engineer')"
        data-type="string"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="warehouse_manager_name"
        :filter-operations="['contains']"
        :caption="$t('stock_management.storage_losses.warehouse_manager')"
        data-type="string"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="total_drying_quantity"
        :filter-operations="['contains']"
        :caption="$t('stock_management.storage_losses.drying_quantity')"
        data-type="number"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="total_cleaning_quantity"
        :filter-operations="['contains']"
        :caption="$t('stock_management.storage_losses.cleaning_quantity')"
        data-type="number"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="total_quantity"
        :filter-operations="['contains']"
        :caption="$t('common.quantity')"
        data-type="number"
        :allow-filtering="true"
      />
      <DxColumn
        data-field="measure_unit"
        :filter-operations="['contains']"
        :caption="$t('common.measure_unit')"
        data-type="string"
        :allow-filtering="true"
      />

      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.storageLossGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="false"
          :can-delete="canDelete"
          :edit-action="goToEdit"
          :merge-with-default-items="true"
          :items="getActionItems(data.data)"
        />
      </template>

      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[15, 50, 100]"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
} from "devextreme-vue/data-grid";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { canDelete, canEdit, canView, permissions, permissionTypes } from "@/helpers/Permissions";
import { errorMessage, successMessage, handlePromiseException  } from "@/helpers/common";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";

export default {
  name: "StorageLossList",
  components: {
    GridPanelHeader,
    DxActionMenu,
    Forbidden,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
  },
  data() {
    return {
      dataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const limit = loadOptions.take || 15;
          const page = Math.floor((loadOptions.skip || 0) / limit) + 1;
          const response = await this.getSmStorageLosses({
            page,
            limit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          });
          return {
            data: response.data.data || [],
            totalCount: response.pagination?.total || 0,
          };
        },
        remove: this.onDelete,
      }),
    };
  },
  computed: {
     ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_STORAGE_LOSSES,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    canView() {
      return canView(this);
    },
  },
  methods: {
    ...mapActions({
      getSmStorageLosses: "smStorageLosses/getSmStorageLosses",
      getExportInfo: "smTransactions/getExportInfo",
      downloadPdfBlob: "smTransactions/downloadPdf",
      deleteSmStorageLoss: "smStorageLosses/deleteSmStorageLoss",
    }),
    goToCreate() {
      this.$router.push({ name: "storage-losses-edit", params: { uuid: "add" } });
    },
    goToEdit(row) {
      if (!row?.uuid) return;
      this.$router.push({ name: "storage-losses-edit", params: { uuid: row.uuid } });
    },
    async onDelete(rowUUid) {
      if (!rowUUid) return;
      try {
        await this.deleteSmStorageLoss({ uuid: rowUUid });
        successMessage(this, this.$t("delete_success"));
        this.$refs.storageLossGrid?.instance?.refresh();
      } catch (e) {
        handleError(this, e);
      }
    },
    async downloadPdf(row) {
      if (!row?.download_url || !row?.document_uuid) return;
      try {
        await this.downloadPdfBlob({
          type: row.document_uuid,
          url: row.download_url,
          filename: row.download_filename,
          response_type: "blob",
        });
      } catch (e) {
        errorMessage(this, this.$t("error_alert_text"));
      }
    },
    getActionItems(row) {
      const items = [];
      if (row?.transaction_uuid && row?.export_url && !row?.document_uuid) {
        items.push({
          key: "generate_pdf",
          label: this.$t("stock_management.transactions.generate_pdf", { type: this.$t("stock_management.storage_losses.title") }),
          text: this.$t("stock_management.transactions.generate_pdf", { type: this.$t("stock_management.storage_losses.title") }),
          icon: require("@/assets/images/icons/transfer.svg"),
          action: () => this.generatePdf(row),
        });
      }
      return items;
    },
    async generatePdf(row) {
      try {
        const response = await this.getExportInfo({
          type: "DECOMMISSION",
          url: row.export_url,
          response_type: "application/json",
        });
        const tx = response.data?.transaction;
        if (tx?.download_url && tx?.document_uuid) {
          await this.downloadPdfBlob({
            type: tx.document_uuid,
            url: tx.download_url,
            filename: tx.download_filename,
            response_type: "blob",
          });
        }
        this.$refs.storageLossGrid?.instance?.refresh();
      } catch (e) {
        handlePromiseException(this, e);
        //errorMessage(this, this.$t("error_alert_text"));
      }
    },
     onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        const instance = this.$refs?.storageLossGrid?.instance;
        if (instance) {
          instance.option("paging.pageIndex", 0);
        }
        this.$refs.storageLossGrid?.instance?.refresh();
      }
    },
    onExporting(e) {
      e.fileName = "SMStorageLosses_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("SMStorageLosses");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (!gridCell?.data) return;
            if (gridCell?.column?.dataField === "is_active") {
              excelCell.value = this.getActiveStatusName(gridCell?.data?.is_active);
            }
            if (["drying_percent", "cleaning_percent", "loss_percent"].includes(gridCell?.column?.dataField)) {
              excelCell.value = Number(gridCell?.value || 0);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${e.fileName}.xlsx`);
          });
        });
      }
    },
  },
};
</script>

<style scoped>
.transaction-pdf-link {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
</style>
